/**
 * Primary-to-Excess Endorsement Carry-Over
 *
 * Every endorsement from the primary policy that the underwriter has INCLUDED
 * should appear in the excess participant coverage dropdown under a new
 * "Endorsements from primary policy" section — not only the ones a user
 * manually gave a limit (per Pam/Jose). This helper filters
 * the quote-level endorsement list down to those that qualify and mirrors the
 * primary tab's limit display: non-premium-bearing endorsements show
 * "Non-premium bearing" instead of a dollar amount, so we carry the flag over.
 */

export interface PrimaryEndorsementCarryOverInput {
  /** The `primary` flag on the endorsement rule. */
  primary: boolean | null | undefined;
  /** Whether the endorsement is included in the primary quote. */
  included: boolean | null | undefined;
  /** The limit set on the endorsement in the primary quote (may be null/0). */
  limit: number | null | undefined;
  /** Whether the endorsement is non-premium-bearing (no dollar limit shown). */
  nonPremiumBearing: boolean | null | undefined;
  /** Endorsement name. */
  name: string | null | undefined;
  /** Endorsement uuid. */
  uuid: string | null | undefined;
}

export interface PrimaryEndorsementCarryOverResult {
  uuid: string;
  name: string;
  limit: number | null;
  nonPremiumBearing: boolean;
}

/**
 * Returns true when a primary endorsement should be carried over to the
 * excess participant coverage section.
 *
 * Rules:
 * - Must be flagged as `primary`
 * - Must be `included` in the primary quote
 * - Must have a `uuid`
 *
 * The limit is intentionally NOT part of the filter: all included primary
 * endorsements carry over, whether or not they have a dollar limit.
 */
export const shouldCarryOverPrimaryEndorsement = (
  input: PrimaryEndorsementCarryOverInput
): boolean => {
  return (
    Boolean(input.primary) && Boolean(input.included) && Boolean(input.uuid)
  );
};

/**
 * Filters a list of endorsements and returns only those that qualify for
 * carry-over from primary to excess, preserving each endorsement's limit and
 * non-premium-bearing flag so the excess row can mirror the primary display.
 */
export const getPrimaryEndorsementsForExcess = (
  endorsements: PrimaryEndorsementCarryOverInput[]
): PrimaryEndorsementCarryOverResult[] => {
  return endorsements.filter(shouldCarryOverPrimaryEndorsement).map((e) => ({
    uuid: e.uuid as string,
    name: e.name ?? 'N/A',
    limit: e.limit ?? null,
    nonPremiumBearing: Boolean(e.nonPremiumBearing),
  }));
};

/**
 * Returns the text shown in the limit column for a carried-over primary
 * endorsement, mirroring the primary tab's display:
 * - non-premium-bearing → "Non-premium bearing"
 * - no limit set (null) → "N/A"
 * - otherwise → the formatted dollar amount
 *
 * `formatLimit` is injected so this helper stays pure and free of UI imports.
 */
export const getEndorsementLimitDisplay = (
  endorsement: Pick<
    PrimaryEndorsementCarryOverResult,
    'nonPremiumBearing' | 'limit'
  >,
  formatLimit: (amount: number) => string
): string => {
  if (endorsement.nonPremiumBearing) {
    return 'Non-premium bearing';
  }
  if (endorsement.limit == null) {
    return 'N/A';
  }
  return formatLimit(endorsement.limit);
};
